/**
 * DateTimeFieldPicker
 *
 * A date and a time side by side, both on the shared field primitives so they
 * carry the same chrome as every other field in the app. The time half used to
 * be a bare `TextInput` in a hand-rolled bordered box, with its own colon
 * insertion and its own clamping — so it sat next to the date looking like a
 * different control, and behaved like one too. `MoonTimeField` already owns that
 * behaviour (`formatTimeTyping`, `clampTime`, a native time input on mobile web),
 * and an empty label renders nothing in `MoonFormField`, so the two stay aligned.
 */

import { useCallback, useMemo, useState } from "react";
import { View } from "react-native";
import { DateField, MoonTimeField } from "@louise/moon-ui";

interface DateTimeFieldPickerProps {
  value: string | null;
  onChange: (isoString: string | null) => void;
}

/** A value that carries a day and no time, the way a phase with no start time is stored. */
const DATE_ONLY = /^(\d{4})-(\d{2})-(\d{2})$/;

export function DateTimeFieldPicker({
  value,
  onChange,
}: DateTimeFieldPickerProps) {
  /**
   * The value, and whether it carries a time at all.
   *
   * `new Date("2026-08-24")` is parsed as midnight **UTC** while every getter
   * below is local, so west of Greenwich a date-only value rendered as the day
   * before — 2026-08-24 came back as the 23rd at 20:00 in New York — and a later
   * date edit recombined the shifted day and saved a transfer one day early.
   * East of it the day held but a phantom 02:00 appeared in a field that should
   * have been empty. Date-only values are therefore built from their parts, in
   * local time, and remembered as time-less so the time field can stay blank.
   */
  const parsedValue = useMemo(() => {
    if (!value || typeof value !== "string") {
      return { date: null as Date | null, hasTime: false };
    }
    const dateOnly = DATE_ONLY.exec(value);
    if (dateOnly) {
      const date = new Date(
        Number(dateOnly[1]),
        Number(dateOnly[2]) - 1,
        Number(dateOnly[3]),
      );
      return {
        date: isNaN(date.getTime()) ? null : date,
        hasTime: false,
      };
    }
    const date = new Date(value);
    return { date: isNaN(date.getTime()) ? null : date, hasTime: true };
  }, [value]);

  const dateValue = parsedValue.date;

  const dateString = useMemo(() => {
    if (!dateValue) return "";
    const y = dateValue.getFullYear();
    const m = String(dateValue.getMonth() + 1).padStart(2, "0");
    const d = String(dateValue.getDate()).padStart(2, "0");
    return `${y}-${m}-${d}`;
  }, [dateValue]);

  const timeString = useMemo(() => {
    if (!dateValue || !parsedValue.hasTime) return "";
    const hours = dateValue.getHours().toString().padStart(2, "0");
    const minutes = dateValue.getMinutes().toString().padStart(2, "0");
    return `${hours}:${minutes}`;
  }, [dateValue, parsedValue.hasTime]);

  // Mirrors what the prop says while the field is untouched, without discarding
  // what she is part-way through typing — the same guard `MoonTimeField` keeps
  // over its own buffer.
  const [timeInput, setTimeInput] = useState(timeString);
  const [syncedTime, setSyncedTime] = useState(timeString);
  if (timeString !== syncedTime) {
    setSyncedTime(timeString);
    setTimeInput(timeString);
  }

  const handleDateChange = useCallback(
    (dateStr: string) => {
      if (!dateStr) {
        onChange(null);
        return;
      }
      // No time on the value means no time in the result. Defaulting to 12:00
      // here put an hour back on a value she had deliberately cleared — and
      // invented one on a date she had only just picked.
      if (!timeInput) {
        onChange(dateStr);
        return;
      }
      const [y, m, d] = dateStr.split("-").map(Number);
      const date = new Date(y!, m! - 1, d!);
      const [hours, minutes] = timeInput.split(":").map(Number);
      date.setHours(hours ?? 12, minutes ?? 0, 0, 0);
      onChange(date.toISOString());
    },
    [timeInput, onChange],
  );

  const handleTimeChange = useCallback(
    (next: string) => {
      setTimeInput(next);
      // Clearing the time has to reach the value, or the field shows empty while
      // the stored ISO still carries the old time. Emitting the bare date is how
      // a time-less value is represented everywhere else
      // (`deriveStepProgress` writes `startDate` alone when a phase has no
      // `startTime`), and the save path parses it.
      if (!next) {
        if (dateString) onChange(dateString);
        return;
      }
      const [hours, minutes] = next.split(":").map(Number);
      const combined = new Date(dateValue ?? new Date());
      combined.setHours(hours ?? 0, minutes ?? 0, 0, 0);
      onChange(combined.toISOString());
    },
    [dateValue, dateString, onChange],
  );

  return (
    <View className="flex-row gap-2.5">
      <View style={{ flex: 1 }}>
        {/* No fallback placeholder. `DateField` shows the user's own date
            format when empty (`placeholderFor(dateFormat)`), which tells her how
            to type it; "Select a date" replaced that with something she already
            knew. */}
        <DateField label="" value={dateString} onChange={handleDateChange} />
      </View>
      <View style={{ flex: 1 }}>
        <MoonTimeField label="" value={timeInput} onChange={handleTimeChange} />
      </View>
    </View>
  );
}
